import { computed } from 'vue';
import type {
  DashboardMapData,
  DashboardMapFocus,
  DashboardMapItem,
} from '@/core/interfaces/Dashboard';
import { useDashboardQuery } from '@/core/hooks/useDashboardQuery';
import { useDashboardStore } from '@/core/hooks/useDashboardStore';
import { useDashboardScope } from '@/core/hooks/useDashboardScope';
import { DashboardService } from '@/core/services/Dashboard.service';
import {
  DASHBOARD_DEFAULT_MAP_CENTER,
  DASHBOARD_DEFAULT_MAP_ZOOM,
} from '@/core/constants/dashboard.constants';
import { filterLocatedMarkers } from '@/core/helpers/dashboard-map';

/**
 * Map widget data + drill-down helpers. Authorization stays outside VietnamMapWidget.
 */
export function useDashboardMap() {
  const { scope, mapConfig, selectedMarkerId } = useDashboardStore();
  const { drillDownToMarker } = useDashboardScope();

  const query = useDashboardQuery<DashboardMapData>('map', scope, (s) =>
    DashboardService.getMap(s)
  );

  const mapMode = computed<'units' | 'warehouses'>(
    () => query.data.value?.mode ?? 'units'
  );

  const markers = computed<DashboardMapItem[]>(() =>
    filterLocatedMarkers(
      query.data.value?.items ?? query.data.value?.markers ?? []
    )
  );

  const unlocatedCount = computed(
    () => query.data.value?.unlocatedCount ?? 0
  );

  const unlocatedItems = computed(
    () => query.data.value?.unlocatedItems ?? []
  );

  const mapFocus = computed<DashboardMapFocus>(() => {
    if (query.data.value?.focus) {
      return query.data.value.focus;
    }
    const center = mapConfig.value.center ?? DASHBOARD_DEFAULT_MAP_CENTER;
    return {
      type: scope.value?.type ?? 'national',
      center: {
        latitude: center.latitude ?? center.lat ?? DASHBOARD_DEFAULT_MAP_CENTER.latitude,
        longitude: center.longitude ?? center.lng ?? DASHBOARD_DEFAULT_MAP_CENTER.longitude,
        lat: center.latitude ?? center.lat,
        lng: center.longitude ?? center.lng,
      },
      zoom: mapConfig.value.zoom ?? DASHBOARD_DEFAULT_MAP_ZOOM,
      provinceCode: scope.value?.provinceCode ?? null,
    };
  });

  function selectMarker(marker: DashboardMapItem): void {
    drillDownToMarker(marker);
  }

  return {
    ...query,
    mapMode,
    markers,
    unlocatedCount,
    unlocatedItems,
    mapFocus,
    selectedMarkerId,
    selectMarker,
  };
}
